import {ArrowNarrowLeftIcon} from '@heroicons/react/solid';
import type {JSX, ReactNode} from 'react';
import {Fragment, useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';

import Spinner from '@/assets/svg/Spinner';
import {Badge, Header} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import {transactionService} from '@/services/transactionService';
import {statusColors} from '@/types/Transaction';
import {capitalizeWords} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';
import {logger} from '@/utils/logger';

import {getProcessorTxFieldsToDisplay} from './summaryFields';
import type {
  AfriexProcessorTransaction,
  AfriexProcessorTxParams,
} from './types';
import {AfriexPaymentProcessors} from './types';

export default function TransactionDetailsPage(): JSX.Element {
  const navigate = useNavigate();
  const {transactionId} = useParams();
  const [searchParams] = useSearchParams();
  const processor = searchParams.get('processor');
  const type = searchParams.get('type');
  const country = searchParams.get('country');
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const [transactionDetail, setTransactionDetail] =
    useState<AfriexProcessorTransaction>({} as AfriexProcessorTransaction);

  const [loadingTnxDetail, setLoadingTnxDetail] = useState<boolean>(false);

  const fetchTransactionDetails = useCallback(async () => {
    try {
      setLoadingTnxDetail(true);

      // Create query parameters for the getSingleProcessorTransaction method
      const queryParams: Partial<AfriexProcessorTxParams> = {};
      if (type) {
        queryParams.type = type;
      }

      if (country) {
        queryParams.country = country;
      }

      // Using the getSingleProcessorTransaction method to fetch transaction details
      const currentTransaction =
        await transactionService.getSingleProcessorTransaction(
          processor || AfriexPaymentProcessors.TRICE,
          transactionId as string,
          queryParams,
        );
      setTransactionDetail(currentTransaction);

      setLoadingTnxDetail(false);
    } catch (_err) {
      setLoadingTnxDetail(false);
    }
    // Depend on the primitive values pulled out of params/searchParams, not
    // the params/searchParams objects themselves: react-router-dom v7 (this
    // component's runtime, bumped in #5685) returns a new object/array
    // reference from useParams()/useSearchParams() on every render, so
    // depending on the objects directly turned this into an infinite
    // fetch-render loop under v7 (verified: ~340 calls within 100ms in a
    // scratch repro) even though the actual param values never changed.
  }, [transactionId, processor, type, country]);

  useEffect(() => {
    // `fetchTransactionDetails` try/catches into `setLoadingTnxDetail(false)`,
    // so this only fires if that catch block itself throws. Attached so such a
    // throw is not an unhandled rejection escaping an effect, and sent to the
    // dev logger because this screen has no error surface of its own.
    fetchTransactionDetails().catch(logger.error);
  }, [fetchTransactionDetails]);

  // Annotated here (rather than on `getProcessorTxFieldsToDisplay` itself,
  // which this screen does not own) so `Object.values(entry)` below resolves
  // through the indexed-type overload instead of TypeScript's untyped
  // `Object.values(o: {}): any[]` fallback, which is what a union of
  // differently-keyed object literals falls back to.
  const fieldsToDisplay: Record<string, ReactNode>[] =
    getProcessorTxFieldsToDisplay(transactionDetail, countryList);

  return (
    <div className="mt-10 mb-5 mx-4 md:mx-10">
      <Header
        title="Transaction Details"
        icon={
          <ArrowNarrowLeftIcon
            className="font-light w-5 h-5 text-black hover:text-indigo-500 cursor-pointer"
            onClick={() => navigate(-1)}
          />
        }
      />

      <div className="flex justify-center items-center flex-col">
        {loadingTnxDetail ? (
          <Spinner size={8} className="text-cyan-600" />
        ) : (
          <div className="mt-10 w-10/12 rounded-md bg-white border border-gray-300 p-7 flex flex-col">
            {transactionDetail && (
              <main>
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <h2 className="font-semibold text-lg mr-3">
                      {transactionDetail.amount} {transactionDetail.asset}
                    </h2>
                    {transactionDetail.status && (
                      <div>
                        <Badge
                          label={transactionDetail.status}
                          colorScheme={statusColors[transactionDetail.status]}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex divide-x">
                    <span className="text-md text-gray-500 font-medium">
                      {formatDate.DDMonYYYY(
                        transactionDetail.createdAt?.toString(),
                      )}
                    </span>
                  </div>
                </div>

                <div className="bg-white mt-10 w-full">
                  {/* TransactionDetailTableHeader */}
                  <>
                    <div className="w-full flex justify-between items-center font-semibold">
                      <h2>
                        {capitalizeWords(
                          transactionDetail.type?.replace(/_/g, ' '),
                        )}{' '}
                        Transaction
                      </h2>
                      <div className="flex gap-4 items-center">
                        <div className="text-cyan-700 uppercase">
                          {transactionDetail.processor || 'N/A'}
                        </div>
                      </div>
                    </div>
                    <hr className="border-1 border-gray-200 mt-2 mb-5 w-full" />
                  </>

                  {/* TransactionDetailTable */}
                  <div className="w-full">
                    {fieldsToDisplay.map((entry, idx) => {
                      const key = Object.keys(entry)[0];
                      const value = Object.values(entry)[0];

                      if (
                        !value ||
                        (typeof value === 'string' && value.length === 0)
                      ) {
                        return null;
                      }

                      return (
                        <Fragment key={`container-${idx}`}>
                          <div
                            key={idx}
                            className="flex justify-between py-2 border-b last:border-0"
                          >
                            <p className="text-slate-500 text-sm">{key}</p>
                            <p className="text-sm font-medium text-gray-700 w-1/2 whitespace-normal overflow-hidden">
                              {value}
                            </p>
                          </div>
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
              </main>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
